import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Quiz as QuizIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AiAutofillPayload,
  EasyFlexDocument,
  EasyFlexWorker,
  checkEasyflexLink,
  createWorkerRequest,
  downloadEasyFlexDocument,
  fetchEasyFlexWorkerById,
  fetchWorkerDocuments,
  requestAiAutofill,
} from '../api/workersApi';
import {
  AnswerTypeFrontend,
  QuestionTypeFrontend,
  SessionQuestionEntityFrontend,
  bulkUpdateSessionQuestionAnswers,
  BulkUpdateAnswerPayloadItem,
} from '../api/sessionQuestionsApi';
import { questionsApi } from '../api/questionsApi';
import {
  FILE_TYPE_LABELS,
  FileType,
  UploadFileData,
  uploadMultipleWorkerFiles,
} from '../api/workerFilesApi';
import { SettingsContext } from '../context/SettingsContext';
import {
  AddWorkerRequest,
  WorkerAvailabilityStatus,
  WorkerStatus,
} from '../models/worker';
import { Profession } from '../models/settings';
import { AnswerType, QuestionDto, QuestionType } from '../types/questions';
import ListItemEditModal from '../components/Worker/ListItemEditModal';
import { extractErrorMessage } from '../utils/errorHandler';

type AiStepKey = 'documents' | 'profession' | 'questions' | 'filling';

interface FileUploadItem {
  id: string;
  file: File;
  fileType: FileType;
}

interface AiResultSummary {
  professionName?: string;
  professionConfidence?: number;
  questionsAnswered: number;
  fieldsUpdated: number;
  totalSuggestions: number;
  aiSuggestionCount: number;
  nonAiSuggestionCount: number;
  processedBy?: string;
}

const AI_STEPS: Array<{ key: AiStepKey; label: string }> = [
  { key: 'documents', label: 'Reading documents & context...' },
  { key: 'profession', label: 'Identifying best profession...' },
  { key: 'questions', label: 'Analyzing session questions...' },
  { key: 'filling', label: 'Filling answers...' },
];

const UPLOAD_FILE_TYPES: FileType[] = [
  FileType.CV,
  FileType.CERTIFICATION,
  FileType.RIJBEWIJS,
  FileType.ID_DOCUMENT,
  FileType.OTHER,
];

const COMMON_NATIONALITIES = [
  'Dutch',
  'Polish',
  'Romanian',
  'German',
  'Hungarian',
  'Bulgarian',
  'Spanish',
  'Italian',
  'French',
  'Portuguese',
  'Turkish',
  'Czech',
  'Slovak',
  'Croatian',
  'Lithuanian',
  'Latvian',
  'Estonian',
  'Belgian',
  'British',
  'Ukrainian',
];

function safeParseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatAddress(addr?: {
  street?: string;
  houseNumber?: string;
  addition?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}): string {
  if (!addr) return '';
  const line1 = [addr.street, addr.houseNumber, addr.addition].filter(Boolean).join(' ');
  const line2 = [addr.postalCode, addr.city].filter(Boolean).join(' ');
  return [line1, line2, addr.country].filter(Boolean).join(', ');
}

/**
 * Editable variant: SessionQuestionEntityFrontend no longer carries `answer` (PR1
 * worker-as-truth). For the AI-suggestion edit flow we extend the type with a
 * transient `answer` field that's submitted to the bulk-update API on save.
 */
type EditableSessionQuestion = SessionQuestionEntityFrontend & { answer?: any };

function questionDtoToSessionEntity(
  q: QuestionDto,
  answer: string | undefined,
): EditableSessionQuestion {
  return {
    id: q.id ?? 0,
    sessionId: 0,
    workerId: '',
    questionId: q.id ?? 0,
    question: q.question,
    questionKey: q.questionKey,
    questionDataKey: q.questionDataKey as unknown as SessionQuestionEntityFrontend['questionDataKey'],
    questionType: q.questionType as unknown as QuestionTypeFrontend,
    answerType: q.answerType as unknown as AnswerTypeFrontend,
    options: q.options?.map(o => ({
      key: o.key,
      label: o.label,
      answerType: q.answerType as unknown as AnswerTypeFrontend,
      isRequiredAnswer: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    })),
    answer: answer || undefined,
    questionShorten: q.questionShorten,
    isRequiredAnswer: q.isRequiredAnswer,
    listItemSchema: q.listItemSchema?.map(f => ({
      fieldName: f.fieldName,
      label: f.label,
      fieldType: f.fieldType as unknown as AnswerTypeFrontend,
      isRequired: f.isRequired,
    })),
    minItems: q.minItems,
    maxItems: q.maxItems,
    requiresVerification: q.requiresVerification,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Strip the EasyFlex worker record down to fields the AI autofill flow uses.
 * Drops internal central-db identifiers / sync metadata / null fields so the
 * AI prompt stays focused on signal. Always sends every populated field —
 * the per-field selection UI was removed (no product value, only complexity).
 */
function buildAiWorkerInput(worker: EasyFlexWorker): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    id: worker.id,
    firstName: worker.firstName,
    lastName: worker.lastName,
    easyflexRegistrationNumber: worker.easyflexRegistrationNumber,
  };

  if (worker.email) payload.email = worker.email;
  if (worker.phone) payload.phone = worker.phone;
  if (worker.nationality) payload.nationality = worker.nationality;
  if (worker.dateOfBirth) payload.dateOfBirth = worker.dateOfBirth;
  if (worker.gender) payload.gender = worker.gender;
  if (worker.postalAddress) payload.postalAddress = worker.postalAddress;
  if (worker.residentialAddress) payload.residentialAddress = worker.residentialAddress;
  if (worker.currentOccupation) payload.currentOccupation = worker.currentOccupation;
  if (worker.currentOccupationTranslated) payload.currentOccupationTranslated = worker.currentOccupationTranslated;
  if (worker.skills) payload.skills = worker.skills;
  if (worker.certifications) payload.certifications = worker.certifications;
  if (worker.languages) payload.languages = worker.languages;
  if (worker.currentPlacement) payload.currentPlacement = worker.currentPlacement;
  if (worker.placements) payload.placements = worker.placements;
  if (worker.driverLicense) payload.driverLicense = worker.driverLicense;
  if (worker.workPermit) payload.workPermit = worker.workPermit;

  return payload;
}

const ImportEasyFlexWorkerPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { easyflexWorkerId } = useParams<{ easyflexWorkerId: string }>();
  const { settings } = useContext(SettingsContext);

  const routeState = (location.state as { worker?: EasyFlexWorker } | null) ?? null;
  const [worker, setWorker] = useState<EasyFlexWorker | null>(routeState?.worker ?? null);
  // Duplicate guard: the local worker already linked to this EasyFlex
  // profile, if one exists. Blocks submit — importing the same regnum(+org)
  // twice silently creates a second worker pointing at the same profile.
  const [existingLink, setExistingLink] = useState<{ workerId: string; firstName?: string | null; lastName?: string | null } | null>(null);
  const [loadingWorker, setLoadingWorker] = useState(!routeState?.worker);

  const professions = useMemo(() => settings?.professions ?? [], [settings?.professions]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [languages, setLanguages] = useState<string[]>([]);
  const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null);
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus>(WorkerStatus.INCOMPLETE);
  const [workerAvailability, setWorkerAvailability] = useState<WorkerAvailabilityStatus>(WorkerAvailabilityStatus.UNAVAILABLE);
  const [memo, setMemo] = useState('');

  const [personalQuestions, setPersonalQuestions] = useState<QuestionDto[]>([]);
  const [professionQuestions, setProfessionQuestions] = useState<QuestionDto[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});

  const [uploadFiles, setUploadFiles] = useState<FileUploadItem[]>([]);
  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const [easyflexDocs, setEasyflexDocs] = useState<EasyFlexDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  // Distinguishes "worker has no documents" from "the fetch FAILED" — the
  // preflight modal previously showed 'No documents on file' for both, so
  // users skipped docs the AI should have seen. Retry bumps the nonce.
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docsRetryNonce, setDocsRetryNonce] = useState(0);
  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(new Set());

  const [aiDocumentSelection, setAiDocumentSelection] = useState<Record<number, boolean>>({});
  // Opens the "Analyze worker with AI" pre-flight modal. The user picks which
  // EasyFlex docs (if any) to feed alongside the worker profile before the AI
  // run kicks off — no free-text input required, since the worker payload
  // already carries everything we know about them.
  const [aiPreflightOpen, setAiPreflightOpen] = useState(false);

  const [aiContextText, setAiContextText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  const [aiStep, setAiStep] = useState<AiStepKey>('documents');
  const [aiCompleted, setAiCompleted] = useState(false);
  const [aiResultSummary, setAiResultSummary] = useState<AiResultSummary | null>(null);
  const pendingAiAnswersRef = useRef<Record<string, string> | null>(null);
  const pendingAiFilledRef = useRef<Set<string> | null>(null);
  // Which profession the pending AI answers were generated FOR. The flush
  // only applies them when that profession's questions actually load —
  // if the user manually switches professions in between, the payload is
  // discarded instead of landing on the wrong profession's question set.
  const pendingAiProfessionKeyRef = useRef<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [listEditModalOpen, setListEditModalOpen] = useState(false);
  const [listEditQuestion, setListEditQuestion] = useState<QuestionDto | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allQuestions = useMemo(() => {
    const byKey = new Map<string, QuestionDto>();
    for (const q of personalQuestions) byKey.set(q.questionKey, q);
    for (const q of professionQuestions) byKey.set(q.questionKey, q);
    return Array.from(byKey.values());
  }, [personalQuestions, professionQuestions]);

  const historicalPlacements = useMemo(() => {
    if (!worker?.placements || worker.placements.length === 0) return [];
    const currentId = worker.currentPlacement?.id;
    return worker.placements.filter(p => p.id !== currentId);
  }, [worker]);

  // Phone is optional for now (backend mirrors this) — many EasyFlex workers
  // have no phone on file. We still warn on bad format if the user DOES type
  // something so the eventual int-format regex on the backend doesn't 400.
  // Follow-up: require email-OR-phone XOR (we always need one way to reach
  // them).
  const phoneError = phoneNumber.trim() !== '' && !/^\+\d{7,15}$/.test(phoneNumber.trim());

  const canSubmit = useMemo(
    () =>
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      selectedProfession !== null &&
      !phoneError &&
      existingLink === null,
    [firstName, lastName, selectedProfession, phoneError, existingLink],
  );

  const selectedAiDocNumbers = useMemo(
    () => easyflexDocs.filter(doc => aiDocumentSelection[doc.documentNumber]).map(doc => doc.documentNumber),
    [easyflexDocs, aiDocumentSelection],
  );

  // Pre-flight duplicate check, re-run when the source profile changes.
  useEffect(() => {
    const regnum = worker?.easyflexRegistrationNumber;
    if (regnum == null) {
      setExistingLink(null);
      return;
    }
    let cancelled = false;
    checkEasyflexLink(regnum, worker?.organizationName)
      .then((res) => {
        if (!cancelled) setExistingLink(res.linked && res.worker ? res.worker : null);
      })
      .catch((err) => {
        // Guard is best-effort: a failed check must not block a legitimate
        // import, but log it so a silent 500 doesn't hide real duplicates.
        console.error('EasyFlex link-check failed:', err);
        if (!cancelled) setExistingLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, [worker?.easyflexRegistrationNumber, worker?.organizationName]);

  useEffect(() => {
    const id = String(easyflexWorkerId || '').trim();
    if (!id) return;

    if (worker && String(worker.id) === id) {
      setLoadingWorker(false);
      return;
    }

    let cancelled = false;
    setLoadingWorker(true);

    fetchEasyFlexWorkerById(id)
      .then((data) => {
        if (!cancelled) setWorker(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load EasyFlex worker:', err);
          setError('Failed to load EasyFlex worker data.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingWorker(false);
      });

    return () => {
      cancelled = true;
    };
  }, [easyflexWorkerId, worker]);

  useEffect(() => {
    let cancelled = false;

    questionsApi.getPersonalQuestions()
      .then((data) => {
        if (cancelled) return;
        const onlyPersonal = data.filter((q: QuestionDto) => q.questionType === QuestionType.PERSONAL);
        setPersonalQuestions(onlyPersonal);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load personal questions:', err);
        setPersonalQuestions([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!worker) return;

    setFirstName(worker.firstName || '');
    setLastName(worker.lastName || '');
    setMiddleName('');
    setPrefix('');
    setPhoneNumber(worker.phone || '');
    setEmailAddress(worker.email || '');
    setNationality(worker.nationality || '');
    setDateOfBirth(safeParseDate(worker.dateOfBirth));
    setMemo('');
    setLanguages(worker.languages ?? []);
    setQuestionAnswers({});
    setUploadFiles([]);
    setAiContextText('');
    setAiError(null);
    setAiLoading(false);
    setAiCompleted(false);
    setAiStep('documents');
    setAiFilledFields(new Set());
    setAiResultSummary(null);
    pendingAiAnswersRef.current = null;
    pendingAiFilledRef.current = null;
    pendingAiProfessionKeyRef.current = null;
    setAiDocumentSelection({});
    setError(null);

    const easyflexPrefilled = new Set<string>();
    if (worker.firstName) easyflexPrefilled.add('firstName');
    if (worker.lastName) easyflexPrefilled.add('lastName');
    if (worker.phone) easyflexPrefilled.add('phoneNumber');
    if (worker.email) easyflexPrefilled.add('emailAddress');
    if (worker.nationality) easyflexPrefilled.add('nationality');
    if (worker.dateOfBirth) easyflexPrefilled.add('dateOfBirth');
    if (worker.languages && worker.languages.length > 0) easyflexPrefilled.add('languages');

    const mappedProfessionKey = worker.currentOccupationTranslated?.optimaProfessionKey;
    if (mappedProfessionKey && professions.length > 0) {
      const match = professions.find(p => p.professionKey === mappedProfessionKey);
      setSelectedProfession(match ?? null);
      if (match) easyflexPrefilled.add('profession');
    } else {
      setSelectedProfession(null);
    }
    setPrefilledFields(easyflexPrefilled);

    setWorkerStatus(WorkerStatus.INCOMPLETE);
    setWorkerAvailability(WorkerAvailabilityStatus.UNAVAILABLE);
  }, [worker, professions]);

  // Pre-fill the personal session questions from the EasyFlex worker via each
  // question's questionDataKey (firstName -> PERSONAL_FIRST_NAME etc). We
  // already have these values; making the AI re-derive them is wasted Gemini
  // calls and the source of the "blue glow but empty input" bug — the AI was
  // returning the field name without a value, so the marker fired but no text
  // landed. With this in place the user sees populated answers on page load,
  // and the AI is only ever needed for fields the EasyFlex profile doesn't
  // already cover.
  useEffect(() => {
    if (!worker || personalQuestions.length === 0) return;

    const w: Record<string, unknown> = {
      firstName: worker.firstName,
      lastName: worker.lastName,
      emailAddress: worker.email,
      phoneNumber: worker.phone,
      nationality: worker.nationality,
      dateOfBirth: worker.dateOfBirth,
    };

    const next: Record<string, string> = {};
    for (const q of personalQuestions) {
      const key = q.questionDataKey;
      if (!key) continue;
      const v = w[key];
      if (v === undefined || v === null || v === '') continue;
      next[q.questionKey] = String(v);
    }
    if (Object.keys(next).length === 0) return;
    // Merge — never overwrite a value the user (or a later AI run) already set.
    setQuestionAnswers((prev) => {
      const merged: Record<string, string> = { ...prev };
      for (const [k, v] of Object.entries(next)) {
        if (!merged[k]) merged[k] = v;
      }
      return merged;
    });
  }, [worker, personalQuestions]);

  useEffect(() => {
    if (!worker?.easyflexRegistrationNumber) {
      setEasyflexDocs([]);
      return;
    }

    let cancelled = false;
    setDocsLoading(true);
    setDocsError(null);

    fetchWorkerDocuments(worker.easyflexRegistrationNumber)
      .then((docs) => {
        if (!cancelled) {
          setEasyflexDocs(docs);
          const selectionDefaults: Record<number, boolean> = {};
          docs.forEach(doc => {
            selectionDefaults[doc.documentNumber] = false;
          });
          setAiDocumentSelection(selectionDefaults);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load EasyFlex documents:', err);
          // Surface the failure: silently rendering the "no documents"
          // empty state made users run the AI without docs it should see.
          setDocsError('Failed to load EasyFlex documents.');
          setEasyflexDocs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [worker?.easyflexRegistrationNumber, docsRetryNonce]);

  useEffect(() => {
    if (!selectedProfession) {
      setProfessionQuestions([]);
      return;
    }

    let cancelled = false;
    setQuestionsLoading(true);

    questionsApi.getQuestionsByProfession(selectedProfession.professionKey)
      .then((data) => {
        if (cancelled) return;
        const onlyProfession = data.filter((q: QuestionDto) => q.questionType === QuestionType.PROFESSION);
        setProfessionQuestions(onlyProfession);

        const pendingMatchesLoaded =
          pendingAiProfessionKeyRef.current === selectedProfession.professionKey;
        if (pendingAiAnswersRef.current) {
          if (pendingMatchesLoaded) {
            setQuestionAnswers(prev => ({ ...prev, ...pendingAiAnswersRef.current! }));
          }
          pendingAiAnswersRef.current = null;
        }
        if (pendingAiFilledRef.current) {
          if (pendingMatchesLoaded) {
            setAiFilledFields(pendingAiFilledRef.current);
          }
          pendingAiFilledRef.current = null;
        }
        pendingAiProfessionKeyRef.current = null;
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load profession questions:', err);
        setProfessionQuestions([]);
      })
      .finally(() => {
        if (!cancelled) setQuestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProfession]);

  const aiFieldSx = useCallback((fieldName: string) => {
    const aiFilled = aiFilledFields.has(fieldName);
    const easyflexPrefilled = prefilledFields.has(fieldName);
    if (!aiFilled && !easyflexPrefilled) return {};

    const accent = aiFilled ? theme.palette.info.main : theme.palette.success.main;
    return {
      '& .MuiOutlinedInput-root': {
        bgcolor: alpha(accent, aiFilled ? 0.1 : 0.08),
        boxShadow: `0 0 0 1px ${alpha(accent, 0.4)}, 0 0 0 3px ${alpha(accent, 0.1)}`,
        '& fieldset': {
          borderColor: alpha(accent, 0.55),
        },
        '&:hover fieldset': {
          borderColor: alpha(accent, 0.8),
        },
      },
    };
  }, [aiFilledFields, prefilledFields, theme]);

  const questionWrapperSx = useCallback((questionKey: string) => {
    if (!aiFilledFields.has(`question:${questionKey}`)) return {};
    return {
      p: 1,
      borderRadius: 1.25,
      border: '1px solid',
      borderColor: alpha(theme.palette.info.main, 0.35),
      bgcolor: alpha(theme.palette.info.main, 0.05),
      boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.08)}`,
    };
  }, [aiFilledFields, theme]);

  const setAnswer = useCallback((questionKey: string, value: string) => {
    setQuestionAnswers(prev => ({ ...prev, [questionKey]: value }));
  }, []);

  const appendFiles = useCallback((files: FileList | File[]) => {
    const newFiles: FileUploadItem[] = Array.from(files).map((f: File) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      fileType: FileType.OTHER,
    }));

    setUploadFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleFileAdd = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    appendFiles(files);
    event.target.value = '';
  }, [appendFiles]);

  const handleUploadDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOverUpload(false);
    if (aiLoading) return;
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;
    appendFiles(files);
  }, [aiLoading, appendFiles]);

  const openUploadDialog = useCallback(() => {
    if (aiLoading) return;
    uploadInputRef.current?.click();
  }, [aiLoading]);

  const handleFileRemove = useCallback((id: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleFileTypeChange = useCallback((id: string, fileType: FileType) => {
    setUploadFiles(prev => prev.map(f => f.id === id ? { ...f, fileType } : f));
  }, []);

  const openListEditor = useCallback((q: QuestionDto) => {
    setListEditQuestion(q);
    setListEditModalOpen(true);
  }, []);

  const handleListEditSave = useCallback((updatedAnswer: string) => {
    if (listEditQuestion) {
      setQuestionAnswers(prev => ({ ...prev, [listEditQuestion.questionKey]: updatedAnswer }));
    }
    setListEditQuestion(null);
    setListEditModalOpen(false);
  }, [listEditQuestion]);

  const toggleAiDocumentSelection = useCallback((docNumber: number, checked: boolean) => {
    setAiDocumentSelection(prev => ({ ...prev, [docNumber]: checked }));
  }, []);

  const handleAiAutofill = useCallback(async () => {
    if (!worker) return;

    setAiLoading(true);
    setAiError(null);
    setAiCompleted(false);
    setAiResultSummary(null);
    setAiStep('documents');

    // Transaction semantics: field mutations happen per-suggestion inside the
    // loop below, but the "AI filled" markers only commit at the end. A
    // failure in between would leave changed values with no provenance — and
    // a retry would blend fills from the failed run with fresh ones. Snapshot
    // everything the autofill can touch and restore on ANY failure.
    const preAiSnapshot = {
      nationality,
      dateOfBirth,
      languages,
      questionAnswers: { ...questionAnswers },
      aiFilledFields: new Set(aiFilledFields),
      selectedProfession,
      pendingAnswers: pendingAiAnswersRef.current,
      pendingFilled: pendingAiFilledRef.current,
      pendingProfessionKey: pendingAiProfessionKeyRef.current,
    };

    try {
      const uploadedDocuments: AiAutofillPayload['uploadedDocuments'] = [];
      for (const item of uploadFiles) {
        if (item.file.size > 10 * 1024 * 1024) continue;
        try {
          const buffer = await item.file.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(buffer).reduce((data: string, byte: number) => data + String.fromCharCode(byte), ''),
          );
          uploadedDocuments.push({
            content_base64: base64,
            mime_type: item.file.type || 'application/octet-stream',
            label: item.file.name,
          });
        } catch {
          // ignore file conversion errors
        }
      }

      const aiWorker = buildAiWorkerInput(worker);

      setAiStep('profession');
      const result = await requestAiAutofill({
        worker: aiWorker,
        memo: aiContextText.trim() || undefined,
        professionKey: selectedProfession?.professionKey || undefined,
        uploadedDocuments: uploadedDocuments.length > 0 ? uploadedDocuments : undefined,
        easyflexDocumentNumbers: selectedAiDocNumbers.length > 0 ? selectedAiDocNumbers : undefined,
        sessionLanguage: 'NL',
      });

      setAiStep('questions');

      // One-shot diagnostic: surface the raw shape so we can see when the AI
      // returns empty/null values for fields and we end up with "blue glow but
      // empty input" — happens when the suggestion comes back with the field
      // name but no actual extracted value.
      console.debug('[AI autofill] suggestions:', result.suggestions);

      const aiAnswers: Record<string, string> = {};
      const newFilledFields = new Set(aiFilledFields);
      let fieldsUpdated = 0;
      const aiSuggestionCount = result.suggestions.filter(
        (s) => String(s.source || '').toLowerCase() === 'ai',
      ).length;
      const nonAiSuggestionCount = result.suggestions.length - aiSuggestionCount;

      for (const s of result.suggestions) {
        if (s.field.startsWith('question:')) {
          const qKey = s.field.replace('question:', '');
          const matchingQuestion = allQuestions.find((q) => q.questionKey === qKey);
          // Reject empty / whitespace-only values up front. The AI sometimes
          // returns the field name with an empty string when it has no signal
          // — without this guard the input renders blank but still shows the
          // "AI filled" marker, which looks broken to the user.
          const rawValue = typeof s.suggestedValue === 'string' ? s.suggestedValue : '';
          const hasContent = (() => {
            if (matchingQuestion?.answerType === AnswerType.LIST_OBJECT) {
              try {
                const parsed = JSON.parse(rawValue);
                // `[null]`, `[""]` or `[{}]` are as empty as `[]` — at least
                // one item must carry a real value, or the field renders
                // blank while still showing the "AI filled" marker.
                return (
                  Array.isArray(parsed) &&
                  parsed.some((item) => {
                    if (item == null) return false;
                    if (typeof item === 'string') return item.trim() !== '';
                    if (typeof item === 'object') {
                      return Object.values(item).some(
                        (v) => v != null && String(v).trim() !== '',
                      );
                    }
                    return true;
                  })
                );
              } catch {
                return rawValue.trim() !== '';
              }
            }
            return rawValue.trim() !== '';
          })();
          if (!hasContent) {
            continue;
          }
          aiAnswers[qKey] = rawValue;
          newFilledFields.add(s.field);
          continue;
        }

        switch (s.field) {
          case 'nationality':
            setNationality(s.suggestedValue);
            newFilledFields.add('nationality');
            fieldsUpdated++;
            break;
          case 'dateOfBirth':
            setDateOfBirth(safeParseDate(s.suggestedValue));
            newFilledFields.add('dateOfBirth');
            fieldsUpdated++;
            break;
          case 'languages':
            try {
              const parsed = JSON.parse(s.suggestedValue);
              if (Array.isArray(parsed)) {
                setLanguages(parsed.filter((v): v is string => typeof v === 'string'));
                newFilledFields.add('languages');
                fieldsUpdated++;
              }
            } catch {
              // ignore malformed AI value
            }
            break;
          default:
            break;
        }
      }

      setAiStep('filling');

      let professionName: string | undefined;
      let professionConfidence: number | undefined;
      const professionChanging = result.professionSuggestion
        && result.professionSuggestion.professionKey !== selectedProfession?.professionKey;

      if (result.professionSuggestion) {
        const match = professions.find(p => p.professionKey === result.professionSuggestion!.professionKey);
        if (match) {
          professionName = match.professionName;
          professionConfidence = result.professionSuggestion.confidence;
          newFilledFields.add('profession');

          if (professionChanging) {
            pendingAiAnswersRef.current = aiAnswers;
            pendingAiFilledRef.current = newFilledFields;
            pendingAiProfessionKeyRef.current = match.professionKey;
            setSelectedProfession(match);
          } else {
            if (Object.keys(aiAnswers).length > 0) {
              setQuestionAnswers(prev => ({ ...prev, ...aiAnswers }));
            }
            setAiFilledFields(newFilledFields);
          }
        }
      } else {
        if (Object.keys(aiAnswers).length > 0) {
          setQuestionAnswers(prev => ({ ...prev, ...aiAnswers }));
        }
        setAiFilledFields(newFilledFields);
      }

      setAiResultSummary({
        professionName,
        professionConfidence,
        questionsAnswered: Object.entries(aiAnswers).filter(([qKey, answer]) => {
          const matchingQuestion = allQuestions.find((q) => q.questionKey === qKey);
          if (!matchingQuestion) return true;
          if (matchingQuestion.answerType !== AnswerType.LIST_OBJECT) return true;
          try {
            const parsed = JSON.parse(answer);
            return !Array.isArray(parsed) || parsed.length > 0;
          } catch {
            return answer.trim() !== '';
          }
        }).length,
        fieldsUpdated,
        totalSuggestions: result.suggestions.length,
        aiSuggestionCount,
        nonAiSuggestionCount,
        processedBy: result.processedBy,
      });

      if (result.errors.length > 0) {
        setAiError(`Partial results: ${result.errors.join('; ')}`);
      }

      setAiCompleted(true);
    } catch (err: unknown) {
      console.error('AI autofill failed:', err);
      // Roll back every field the failed run may have touched so the form is
      // exactly as it was before the button was pressed — a retry starts clean.
      setNationality(preAiSnapshot.nationality);
      setDateOfBirth(preAiSnapshot.dateOfBirth);
      setLanguages(preAiSnapshot.languages);
      setQuestionAnswers(preAiSnapshot.questionAnswers);
      setAiFilledFields(preAiSnapshot.aiFilledFields);
      setSelectedProfession(preAiSnapshot.selectedProfession);
      pendingAiAnswersRef.current = preAiSnapshot.pendingAnswers;
      pendingAiFilledRef.current = preAiSnapshot.pendingFilled;
      pendingAiProfessionKeyRef.current = preAiSnapshot.pendingProfessionKey;
      const message = await extractErrorMessage(err);
      setAiError(message ? `${message} — the form was restored to its pre-AI state.` : 'AI autofill failed — the form was restored to its pre-AI state.');
      setAiCompleted(false);
      setAiResultSummary(null);
    } finally {
      setAiLoading(false);
    }
  }, [
    aiContextText,
    aiFilledFields,
    dateOfBirth,
    languages,
    nationality,
    professions,
    questionAnswers,
    selectedAiDocNumbers,
    selectedProfession,
    uploadFiles,
    worker,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!worker || !canSubmit || !selectedProfession) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload: AddWorkerRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        middleName: middleName.trim() || undefined,
        prefix: prefix.trim() || undefined,
        professionKey: selectedProfession.professionKey,
        emailAddress: emailAddress.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        nationality: nationality.trim() || undefined,
        dateOfBirth: dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : undefined,
        workerAvailability,
        status: workerStatus,
        memo: memo.trim() || undefined,
        languages: languages.length > 0 ? languages : undefined,
        // Stage-1 link: pointer to central-db instead of creating an
        // easyflex_workers mirror row.
        easyflexRegistrationNumber: worker?.easyflexRegistrationNumber != null
          ? String(worker.easyflexRegistrationNumber)
          : undefined,
        // organizationName is the sync-source org ("Optima Uitzendburo" /
        // "Optima Work") — the regnum's collision disambiguator. NOT
        // business.name (the employer relation), which only coincides with
        // it in dev seed data.
        easyflexOrganization: worker?.organizationName || undefined,
      };

      const createdWorker = await createWorkerRequest(payload);

      if (uploadFiles.length > 0) {
        const fileUploads: UploadFileData[] = uploadFiles.map(f => ({
          file: f.file,
          workerId: createdWorker.workerId,
          fileType: f.fileType,
        }));
        await uploadMultipleWorkerFiles(fileUploads);
      }

      const answersToSave = Object.entries(questionAnswers)
        .filter(([, value]) => value !== '' && value !== undefined)
        .map(([questionKey, answer]) => {
          const q = allQuestions.find(item => item.questionKey === questionKey);
          return q?.id ? { sessionQuestionId: q.id, answer } : null;
        })
        .filter((a): a is BulkUpdateAnswerPayloadItem => a !== null);

      if (answersToSave.length > 0) {
        await bulkUpdateSessionQuestionAnswers(createdWorker.workerId, answersToSave);
      }

      navigate('/resources/workers', {
        state: {
          openTab: 'incomplete',
          importSuccessMessage: `Successfully imported ${createdWorker.firstName} ${createdWorker.lastName} into recruitment pipeline`,
        },
      });
    } catch (err: unknown) {
      console.error('Import failed:', err);
      const raw = await extractErrorMessage(err).catch(() => null);
      const msg =
        typeof raw === 'string' && raw.trim() !== ''
          ? raw
          : Array.isArray(raw)
            ? (raw as unknown[]).map(String).join('; ')
            : 'Failed to import worker.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    allQuestions,
    canSubmit,
    dateOfBirth,
    emailAddress,
    firstName,
    languages,
    lastName,
    memo,
    middleName,
    navigate,
    nationality,
    phoneNumber,
    prefix,
    questionAnswers,
    selectedProfession,
    uploadFiles,
    worker,
    workerAvailability,
    workerStatus,
  ]);

  const renderQuestionInput = useCallback((question: QuestionDto) => {
    const value = questionAnswers[question.questionKey] ?? '';
    const isAiFilled = aiFilledFields.has(`question:${question.questionKey}`);

    switch (question.answerType) {
      case AnswerType.BOOLEAN:
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === 'true'}
                onChange={(_, checked) => setAnswer(question.questionKey, String(checked))}
              />
            }
            label={value === 'true' ? 'Yes' : 'No'}
          />
        );

      case AnswerType.DATE:
        return (
          <DatePicker
            value={value ? safeParseDate(value) : null}
            onChange={(d: Date | null) => setAnswer(question.questionKey, d ? d.toISOString().split('T')[0] : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: isAiFilled ? aiFieldSx(`question:${question.questionKey}`) : undefined,
              },
            }}
          />
        );

      case AnswerType.RADIO:
        return (
          <FormControl size="small">
            <RadioGroup
              row
              value={value}
              onChange={(e) => setAnswer(question.questionKey, e.target.value)}
            >
              {question.options?.map(opt => (
                <FormControlLabel
                  key={opt.key}
                  value={opt.key}
                  control={<Radio size="small" />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case AnswerType.MULTISELECT:
        return (
          <Autocomplete
            multiple
            size="small"
            options={question.options?.map(o => o.key) ?? []}
            getOptionLabel={(key) => question.options?.find(o => o.key === key)?.label ?? key}
            value={value ? (typeof value === 'string' ? JSON.parse(value || '[]') : value) : []}
            onChange={(_, newVal) => setAnswer(question.questionKey, JSON.stringify(newVal))}
            renderInput={(params) => <TextField {...params} placeholder="Select..." />}
          />
        );

      case AnswerType.NUMBER:
        return (
          <TextField
            type="number"
            size="small"
            fullWidth
            value={value}
            onChange={(e) => setAnswer(question.questionKey, e.target.value)}
            sx={isAiFilled ? aiFieldSx(`question:${question.questionKey}`) : undefined}
          />
        );

      case AnswerType.TEXT_AREA:
        return (
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            value={value}
            onChange={(e) => setAnswer(question.questionKey, e.target.value)}
            sx={isAiFilled ? aiFieldSx(`question:${question.questionKey}`) : undefined}
          />
        );

      case AnswerType.LIST_OBJECT:
        return (
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => openListEditor(question)}
            >
              Edit Items
              {value && (() => {
                try {
                  const arr = JSON.parse(value);
                  return Array.isArray(arr) ? ` (${arr.length})` : '';
                } catch {
                  return '';
                }
              })()}
            </Button>
          </Box>
        );

      case AnswerType.TEXT:
      default:
        return (
          <TextField
            size="small"
            fullWidth
            value={value}
            onChange={(e) => setAnswer(question.questionKey, e.target.value)}
            sx={isAiFilled ? aiFieldSx(`question:${question.questionKey}`) : undefined}
          />
        );
    }
  }, [aiFieldSx, aiFilledFields, openListEditor, questionAnswers, setAnswer]);

  if (loadingWorker) {
    return (
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, display: 'grid', placeItems: 'center' }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Loading EasyFlex worker...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!worker) {
    return (
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>EasyFlex worker could not be loaded.</Alert>
        <Button variant="outlined" onClick={() => navigate('/resources/workers')}>Back to Workers</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/resources/workers')}
              disabled={submitting}
            >
              Back
            </Button>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Import Worker: {worker.firstName} {worker.lastName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip label={worker.status.replace(/_/g, ' ')} size="small" />
                {worker.business && <Chip label={worker.business.name} size="small" variant="outlined" />}
                {worker.easyflexRegistrationNumber && (
                  <Chip label={`EF #${worker.easyflexRegistrationNumber}`} size="small" variant="outlined" color="info" />
                )}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color="default"
              variant="outlined"
              label={`AI docs ON: ${selectedAiDocNumbers.length}`}
            />
          </Stack>
        </Stack>

        {existingLink && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => navigate(`/workers/${existingLink.workerId}`)}>
                Open worker
              </Button>
            }
          >
            This EasyFlex profile is already imported as{' '}
            <strong>{[existingLink.firstName, existingLink.lastName].filter(Boolean).join(' ') || existingLink.workerId}</strong>
            {' '}— importing again would create a duplicate. Submit is disabled.
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.04),
                height: '100%',
              }}
            >
              <SectionHeader icon={<PersonIcon fontSize="small" />} title="EasyFlex Data (Left)" />

              <SectionHeader icon={<PersonIcon fontSize="small" />} title="Personal" />
              <Stack spacing={1} sx={{ mb: 2.5 }}>
                <InfoRow label="Name" value={`${worker.firstName} ${worker.lastName}`} />
                <InfoRow label="Email" value={worker.email} />
                <InfoRow label="Phone" value={worker.phone} />
                <InfoRow label="Nationality" value={worker.nationality || ''} />
                <InfoRow label="Date of Birth" value={worker.dateOfBirth || ''} />
                <InfoRow label="Gender" value={worker.gender || ''} />
                <InfoRow label="Postal Address" value={formatAddress(worker.postalAddress)} />
                <InfoRow label="Residential Address" value={formatAddress(worker.residentialAddress)} />
              </Stack>

              <SectionHeader icon={<WorkIcon fontSize="small" />} title="Professional" />
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.32),
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  EasyFlex Profession
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                  <WorkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Chip
                    size="small"
                    label={worker.currentOccupation || 'N/A'}
                    color={worker.currentOccupation ? 'primary' : 'default'}
                    variant={worker.currentOccupation ? 'filled' : 'outlined'}
                  />
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Mapped Optima Profession
                </Typography>
                <Chip
                  size="small"
                  label={worker.currentOccupationTranslated?.optimaProfessionName || 'No mapping found'}
                  color={worker.currentOccupationTranslated?.optimaProfessionName ? 'success' : 'default'}
                  variant={worker.currentOccupationTranslated?.optimaProfessionName ? 'filled' : 'outlined'}
                />
              </Box>
              <Stack spacing={1} sx={{ mb: 2.5 }}>
                <InfoRow label="EasyFlex Profession (raw)" value={worker.currentOccupation || ''} />
                <InfoRow
                  label="Mapped Optima Profession"
                  value={worker.currentOccupationTranslated?.optimaProfessionName || ''}
                />
                <InfoRow label="Skills" value={(worker.skills || []).join(', ')} />
                <InfoRow label="Certifications" value={(worker.certifications || []).join(', ')} />
                <InfoRow label="Languages" value={(worker.languages || []).join(', ')} />
                <InfoRow
                  label="Current Placement"
                  value={
                    worker.currentPlacement
                      ? `${worker.currentPlacement.organizationName} · ${worker.currentPlacement.functionName}`
                      : ''
                  }
                />
                <InfoRow
                  label="Placement History"
                  value={historicalPlacements.length ? `${historicalPlacements.length} placement(s)` : ''}
                />
                <InfoRow
                  label="Driver License"
                  value={
                    worker.driverLicense?.categories
                      ? Object.entries(worker.driverLicense.categories)
                          .filter(([, enabled]) => enabled)
                          .map(([cat]) => cat)
                          .join(', ')
                      : ''
                  }
                />
                <InfoRow label="Work Permit" value={worker.workPermit?.type || ''} />
              </Stack>

              <SectionHeader icon={<FolderIcon fontSize="small" />} title="EasyFlex Documents" subtitle="Tick which to feed the AI via the Analyze button above" />
              {docsLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Loading documents...
                </Typography>
              )}
              {!docsLoading && docsError && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => setDocsRetryNonce(n => n + 1)}>
                      Retry
                    </Button>
                  }
                >
                  {docsError}
                </Alert>
              )}
              {!docsLoading && !docsError && easyflexDocs.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  No documents found in EasyFlex.
                </Typography>
              )}
              {!docsLoading && easyflexDocs.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {easyflexDocs.length} document{easyflexDocs.length === 1 ? '' : 's'} on file
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 280,
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  >
                    <Stack spacing={0.5}>
                      {easyflexDocs.map((doc) => (
                        <Stack
                          key={doc.documentNumber}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            p: 0.75,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 0.65),
                            border: '1px solid',
                            borderColor: aiDocumentSelection[doc.documentNumber]
                              ? alpha(theme.palette.info.main, 0.45)
                              : 'divider',
                          }}
                        >
                          <DescriptionIcon fontSize="small" color="action" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {doc.subject || doc.fileName || `Document #${doc.documentNumber}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {doc.fileName}
                            </Typography>
                          </Box>
                          {aiDocumentSelection[doc.documentNumber] && (
                            <Chip
                              size="small"
                              icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                              label="AI"
                              color="info"
                              variant="outlined"
                              sx={{ height: 22 }}
                            />
                          )}
                          <Tooltip title={`Download ${doc.fileName || doc.subject}`}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const fname = doc.fileName || `${doc.subject || 'document'}-${doc.documentNumber}`;
                                downloadEasyFlexDocument(doc.documentNumber, fname).catch((err: any) => {
                                  console.error('Document download failed:', err);
                                  if (err?.response?.status === 404) {
                                    // Deleted in EasyFlex; the mirror row was just
                                    // tombstoned — refetch so the ghost disappears.
                                    setDocsError('That document was removed in EasyFlex — refreshing the list.');
                                    setDocsRetryNonce((n) => n + 1);
                                  } else {
                                    setError('Document download failed.');
                                  }
                                });
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1.25 }}
              >
                <SectionHeader icon={<PersonIcon fontSize="small" />} title="Worker Data" subtitle="Core fields + session questions below" />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    aiLoading ? (
                      <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  onClick={() => setAiPreflightOpen(true)}
                  disabled={aiLoading || !worker}
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.secondary.main})`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {aiLoading ? 'Analyzing…' : aiCompleted ? 'Re-analyze with AI' : 'Analyze worker with AI'}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  icon={<CheckCircleIcon />}
                  label="Green glow = prefilled from EasyFlex"
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  icon={<AutoAwesomeIcon />}
                  label="Blue glow = AI filled"
                />
              </Stack>

              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First Name"
                    required
                    fullWidth
                    size="small"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    error={!firstName.trim()}
                    sx={aiFieldSx('firstName')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last Name"
                    required
                    fullWidth
                    size="small"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    error={!lastName.trim()}
                    sx={aiFieldSx('lastName')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Middle Name"
                    fullWidth
                    size="small"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    sx={aiFieldSx('middleName')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Name Prefix"
                    fullWidth
                    size="small"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                    sx={aiFieldSx('prefix')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Phone Number"
                    fullWidth
                    size="small"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    error={phoneError}
                    helperText={
                      phoneError
                        ? 'Must be international format, e.g. +31612345678'
                        : undefined
                    }
                    placeholder="+31612345678"
                    sx={aiFieldSx('phoneNumber')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    size="small"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    sx={aiFieldSx('emailAddress')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <AiFieldLabel
                    label="Nationality"
                    fieldName="nationality"
                    aiFilledFields={aiFilledFields}
                    prefilledFields={prefilledFields}
                  />
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={COMMON_NATIONALITIES}
                    value={nationality}
                    onInputChange={(_, newVal) => setNationality(newVal)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type nationality"
                        sx={aiFieldSx('nationality')}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <AiFieldLabel
                    label="Date of Birth"
                    fieldName="dateOfBirth"
                    aiFilledFields={aiFilledFields}
                    prefilledFields={prefilledFields}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={dateOfBirth}
                      onChange={setDateOfBirth}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: aiFieldSx('dateOfBirth'),
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={workerStatus}
                    onChange={(e) => setWorkerStatus(e.target.value as WorkerStatus)}
                    fullWidth
                  >
                    {(settings?.workerStatuses || Object.values(WorkerStatus)).map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    size="small"
                    label="Availability"
                    value={workerAvailability}
                    onChange={(e) => setWorkerAvailability(e.target.value as WorkerAvailabilityStatus)}
                    fullWidth
                  >
                    {(settings?.availabilityStatuses || Object.values(WorkerAvailabilityStatus)).map((availability) => (
                      <MenuItem key={availability} value={availability}>{availability}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    size="small"
                    options={professions}
                    getOptionLabel={(p) => p.professionName}
                    value={selectedProfession}
                    onChange={(_, newVal) => setSelectedProfession(newVal)}
                    isOptionEqualToValue={(opt, val) => opt.professionKey === val.professionKey}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Profession"
                        required
                        error={!selectedProfession}
                        sx={aiFieldSx('profession')}
                        helperText={
                          worker.currentOccupationTranslated?.optimaProfessionKey
                            ? `Auto-mapped from EasyFlex occupation: ${worker.currentOccupation || 'N/A'}`
                            : 'Select a profession to load questions'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <SectionHeader icon={<QuizIcon fontSize="small" />} title="Session Questions" subtitle="Personal questions always included" />

              {personalQuestions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Personal</Typography>
                  <Stack spacing={1.5}>
                    {personalQuestions.map((q) => (
                      <Box key={q.questionKey} sx={questionWrapperSx(q.questionKey)}>
                        <QuestionLabel question={q} aiFilled={aiFilledFields.has(`question:${q.questionKey}`)} />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          {renderQuestionInput(q)}
                        </LocalizationProvider>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {selectedProfession && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Profession-specific {questionsLoading ? '(loading...)' : `(${professionQuestions.length})`}
                  </Typography>
                  <Stack spacing={1.5}>
                    {professionQuestions.map((q) => (
                      <Box key={q.questionKey} sx={questionWrapperSx(q.questionKey)}>
                        <QuestionLabel question={q} aiFilled={aiFilledFields.has(`question:${q.questionKey}`)} />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          {renderQuestionInput(q)}
                        </LocalizationProvider>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {!selectedProfession && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a profession to load profession-specific questions.
                </Typography>
              )}

              <SectionHeader icon={<NotesIcon fontSize="small" />} title="Comments / Notes" />
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder="Add any notes about this worker..."
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: alpha(theme.palette.info.main, 0.35),
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.info.main, 0.2),
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <AutoAwesomeIcon sx={{ color: 'info.main' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>AI analysis</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Trigger from the "Analyze worker with AI" button above. Add extra files below if the worker has docs not in EasyFlex.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ p: 2 }}>
                <input
                  ref={uploadInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileAdd}
                  disabled={aiLoading}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />

                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragOverUpload
                      ? theme.palette.info.main
                      : alpha(theme.palette.info.main, 0.35),
                    borderRadius: 2,
                    p: 2.25,
                    textAlign: 'center',
                    cursor: aiLoading ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: isDragOverUpload
                      ? alpha(theme.palette.info.main, 0.08)
                      : alpha(theme.palette.info.main, 0.02),
                    minHeight: 112,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(!aiLoading && {
                      '&:hover': {
                        borderColor: theme.palette.info.main,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                      },
                    }),
                  }}
                  onClick={openUploadDialog}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!aiLoading) setIsDragOverUpload(true);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!aiLoading) setIsDragOverUpload(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragOverUpload(false);
                  }}
                  onDrop={handleUploadDrop}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openUploadDialog();
                    }
                  }}
                  role="button"
                  tabIndex={aiLoading ? -1 : 0}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <CloudUploadIcon sx={{ color: 'info.main', fontSize: 32 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Drop files here or click to upload (optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDF, DOC, DOCX, JPG, PNG, GIF · Max 10MB per file
                    </Typography>
                  </Stack>
                </Box>

                {uploadFiles.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    {uploadFiles.map((f) => (
                      <Stack
                        key={f.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.info.main, 0.04),
                        }}
                      >
                        <DescriptionIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                          {f.file.name}
                        </Typography>
                        <TextField
                          select
                          size="small"
                          value={f.fileType}
                          onChange={(e) => handleFileTypeChange(f.id, e.target.value as FileType)}
                          sx={{ minWidth: 130 }}
                        >
                          {UPLOAD_FILE_TYPES.map((ft) => (
                            <MenuItem key={ft} value={ft}>
                              {FILE_TYPE_LABELS[ft]}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton size="small" onClick={() => handleFileRemove(f.id)} disabled={aiLoading}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Collapse in={aiLoading}>
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      sx={{
                        mb: 2,
                        borderRadius: 1,
                        height: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.secondary.main})`,
                        },
                      }}
                    />
                    <Stack spacing={1}>
                      {AI_STEPS.map((step, i) => {
                        const stepIdx = AI_STEPS.findIndex(s => s.key === aiStep);
                        const isDone = stepIdx > i;
                        const isActive = stepIdx === i;
                        return (
                          <Box key={step.key}>
                            <Fade in timeout={350}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                {isDone ? (
                                  <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                ) : isActive ? (
                                  <CircularProgress size={16} sx={{ color: 'info.main' }} />
                                ) : (
                                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDone ? 'success.main' : isActive ? 'info.main' : 'text.disabled',
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                >
                                  {step.label}
                                </Typography>
                              </Stack>
                            </Fade>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>

                {aiError && (
                  <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setAiError(null)}>
                    {aiError}
                  </Alert>
                )}

                <Collapse in={aiCompleted && !!aiResultSummary && !aiLoading}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: aiResultSummary?.aiSuggestionCount
                        ? alpha(theme.palette.success.main, 0.06)
                        : alpha(theme.palette.warning.main, 0.08),
                      border: '1px solid',
                      borderColor: aiResultSummary?.aiSuggestionCount
                        ? alpha(theme.palette.success.main, 0.25)
                        : alpha(theme.palette.warning.main, 0.35),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <CheckCircleIcon
                        sx={{
                          color: aiResultSummary?.aiSuggestionCount ? 'success.main' : 'warning.main',
                          fontSize: 20,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color={aiResultSummary?.aiSuggestionCount ? 'success.main' : 'warning.main'}
                      >
                        {aiResultSummary?.aiSuggestionCount
                          ? 'AI Analysis Complete'
                          : 'Analysis Complete (No AI suggestions returned)'}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.75}>
                      {aiResultSummary?.professionName && (
                        <Typography variant="body2">
                          Profession suggestion: <strong>{aiResultSummary.professionName}</strong>
                          {aiResultSummary.professionConfidence != null && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              ({Math.round(aiResultSummary.professionConfidence * 100)}% match)
                            </Typography>
                          )}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Filled <strong>{aiResultSummary?.questionsAnswered ?? 0}</strong> question(s)
                      </Typography>
                      <Typography variant="body2">
                        Updated <strong>{aiResultSummary?.fieldsUpdated ?? 0}</strong> personal field(s)
                      </Typography>
                      <Typography variant="body2">
                        AI-sourced suggestions: <strong>{aiResultSummary?.aiSuggestionCount ?? 0}</strong>
                        {' '}of{' '}
                        <strong>{aiResultSummary?.totalSuggestions ?? 0}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Processor: {aiResultSummary?.processedBy || 'unknown'}
                      </Typography>
                      {!!(aiResultSummary?.nonAiSuggestionCount) && (
                        <Typography variant="caption" color="warning.main">
                          Non-AI suggestions returned: {aiResultSummary.nonAiSuggestionCount}
                        </Typography>
                      )}
                    </Stack>
                    <Button
                      size="small"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => {
                        setAiCompleted(false);
                        setAiResultSummary(null);
                      }}
                      sx={{ mt: 1.25 }}
                    >
                      Re-analyze
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={() => navigate('/resources/workers')} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Importing...' : 'Save & Import'}
          </Button>
        </Stack>
      </Paper>

      {listEditQuestion && (
        <ListItemEditModal
          open={listEditModalOpen}
          onClose={() => {
            setListEditModalOpen(false);
            setListEditQuestion(null);
          }}
          question={questionDtoToSessionEntity(
            listEditQuestion,
            questionAnswers[listEditQuestion.questionKey],
          )}
          onSave={handleListEditSave}
        />
      )}

      <Dialog
        open={aiPreflightOpen}
        onClose={() => setAiPreflightOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon sx={{ color: 'info.main' }} />
            <span>Analyze worker with AI</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The worker profile (name, occupation, placements, skills, address,
            licenses, etc.) will be sent automatically. Optionally include any
            EasyFlex documents you want the AI to read — CVs and certificates
            usually give the best results.
          </Typography>

          {docsLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading documents…
            </Typography>
          )}

          {!docsLoading && docsError && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => setDocsRetryNonce(n => n + 1)}>
                  Retry
                </Button>
              }
            >
              {docsError} The AI would otherwise run without documents it should see.
            </Alert>
          )}

          {!docsLoading && !docsError && easyflexDocs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No EasyFlex documents on file — the AI will analyze the worker
              profile alone.
            </Typography>
          )}

          {!docsLoading && easyflexDocs.length > 0 && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {selectedAiDocNumbers.length} of {easyflexDocs.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  disabled={selectedAiDocNumbers.length === easyflexDocs.length}
                  onClick={() => {
                    const next: Record<number, boolean> = {};
                    for (const d of easyflexDocs) next[d.documentNumber] = true;
                    setAiDocumentSelection(next);
                  }}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={selectedAiDocNumbers.length === 0}
                  onClick={() => setAiDocumentSelection({})}
                >
                  Clear
                </Button>
              </Stack>

              <Stack spacing={0.5} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {easyflexDocs.map((doc) => (
                  <Stack
                    key={doc.documentNumber}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 0.75,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.background.paper, 0.65),
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={!!aiDocumentSelection[doc.documentNumber]}
                      onChange={(_, checked) =>
                        toggleAiDocumentSelection(doc.documentNumber, checked)
                      }
                    />
                    <DescriptionIcon fontSize="small" color="action" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {doc.subject || doc.fileName || `Document #${doc.documentNumber}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {doc.fileName}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiPreflightOpen(false)} disabled={aiLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setAiDocumentSelection({});
              setAiPreflightOpen(false);
              void handleAiAutofill();
            }}
            disabled={aiLoading}
          >
            Skip docs
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => {
              setAiPreflightOpen(false);
              void handleAiAutofill();
            }}
            disabled={aiLoading}
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.secondary.main})`,
            }}
          >
            {selectedAiDocNumbers.length > 0
              ? `Analyze with ${selectedAiDocNumbers.length} doc${selectedAiDocNumbers.length === 1 ? '' : 's'}`
              : 'Analyze worker only'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">({subtitle})</Typography>}
    </Stack>
  );
}

function AiFieldLabel({
  label,
  fieldName,
  aiFilledFields,
  prefilledFields,
}: {
  label: string;
  fieldName: string;
  aiFilledFields: Set<string>;
  prefilledFields: Set<string>;
}) {
  const isAiFilled = aiFilledFields.has(fieldName);
  const isPrefilled = prefilledFields.has(fieldName);
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.4 }}>
      {isAiFilled && <AutoAwesomeIcon sx={{ fontSize: 15, color: 'info.main' }} />}
      {!isAiFilled && isPrefilled && <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main' }} />}
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function QuestionLabel({ question, aiFilled }: { question: QuestionDto; aiFilled: boolean }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      {aiFilled && <AutoAwesomeIcon sx={{ fontSize: 16, color: 'info.main' }} />}
      <Typography variant="body2" fontWeight={600}>
        {question.questionShorten || question.question}
        {question.isRequiredAnswer && <Typography component="span" color="error.main"> *</Typography>}
      </Typography>
    </Stack>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || 'N/A'}</Typography>
      </Box>
    </Stack>
  );
}

export default ImportEasyFlexWorkerPage;
